import {
  CircleAlert,
  CircleCheck,
  Info,
  LucideIcon,
  TriangleAlert,
} from "lucide-react"
import React, { FC, createElement } from "react"
import { useTranslation } from "react-i18next"
import { usePage } from "@inertiajs/react"
import { HostPageContext, TransientData } from "@/lib/page-rendering"
import AppHeader from "@/components/header"
import { Toaster } from "@/components/ui/sonner"
import * as z from "zod"
import { type ClassValue, cn } from "@/lib/utils"
import ScrollerProvider from "@/components/shared/scroller"

type Children = {
  children: React.ReactNode
}

type HasTestId = {
  testid: string
}

export function StyledDiv(
  ...classValues: ClassValue[]
): FC<React.HTMLProps<HTMLDivElement> & Children & Partial<HasTestId>> {
  return ({ children, testid, className, ...props }) => (
    <div className={cn(classValues, className)} data-testid={testid} {...props}>
      {children}
    </div>
  )
}

export const ScrollingLayout = StyledDiv("w-full", "flex", "flex-col")

export const FullScreenLayout = StyledDiv(
  "absolute",
  "flex",
  "h-full",
  "min-h-0",
  "w-full",
  "flex-col",
)

// The body shares the header's and the chrome's horizontal gutter so their
// edges align at every width, and its top padding matches the chrome's so
// the title has the same space below it as above.
const pageBodyClasses = [
  "container",
  "w-full",
  "px-4",
  "mb-4",
  "flex",
  "flex-col",
  "gap-4",
  "mx-auto",
  "pt-4",
]

const ScrollingPageBody = StyledDiv(...pageBodyClasses, "h-full")

// Inside a full-screen page the body takes the height left beneath the page's
// own rows and owns the overflow, so the document never scrolls and each
// region inside declares its own scrolling.
const FullScreenPageBody = StyledDiv(
  ...pageBodyClasses,
  "flex-1",
  "min-h-0",
  "overflow-hidden",
)

export const AppContent = StyledDiv("grow", "w-full", "relative")

export const Workspace = StyledDiv(
  "flex",
  "flex-col",
  "justify-center",
  "items-stretch",
  "min-h-screen",
  "bg-brand-background",
)

export const Grid = StyledDiv(
  "w-full",
  "grid",
  "grid-cols-1",
  "gap-4",
  "md:grid-cols-2",
  "lg:grid-cols-3",
  "xl:grid-cols-4",
)

export const Section = StyledDiv(
  "p-6",
  "shadow-md",
  "rounded-md",
  "border",
  "border-gray-200",
)

export const SectionTitle = ({
  icon,
  children,
}: { icon: LucideIcon } & Children) => {
  return (
    <div className="mb-4 flex flex-row items-center gap-2">
      {createElement(icon, { className: "h-6 w-6" })}
      <div className="text-xl font-semibold">{children}</div>
    </div>
  )
}

export const TestId: FC<Children & HasTestId> = ({ children, testid }) => (
  <div data-testid={testid} className="contents">
    {children}
  </div>
)

// The shell has no header flag: every page outside an iframe is drawn under
// the header, the Error page included. A page in an iframe is a sheet's and
// draws its own body.
type LayoutContextValue = {
  fullScreen: boolean
  iframe: boolean
}

const LayoutContext = React.createContext<LayoutContextValue>({
  fullScreen: false,
  iframe: false,
})

export const PageBody: typeof ScrollingPageBody = (props) =>
  React.use(LayoutContext).fullScreen ? (
    <FullScreenPageBody {...props} />
  ) : (
    <ScrollingPageBody {...props} />
  )

const ZodConfig = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation("common")
  React.useEffect(() => {
    z.config({
      customError: (iss) => {
        if (iss.code === "too_small") {
          if (iss.origin === "array") {
            return t("errors.required")
          }
        }
        return null
      },
    })
  }, [t])

  return children
}

const WorkspaceLayout = ({ children }: { children: React.ReactNode }) =>
  React.use(LayoutContext).iframe ? children : <Workspace>{children}</Workspace>

const ShowHeader = ({ children }: { children: React.ReactNode }) => (
  <>
    {!React.use(LayoutContext).iframe && <AppHeader />}
    {children}
  </>
)

const BodyLayout = ({ children }: { children: React.ReactNode }) =>
  React.use(LayoutContext).fullScreen ? (
    <FullScreenLayout>{children}</FullScreenLayout>
  ) : (
    <ScrollingLayout>{children}</ScrollingLayout>
  )

const ToasterConfig = ({ children }: { children: React.ReactNode }) => (
  <>
    {children}
    <Toaster
      richColors
      containerAriaLabel="Notifications"
      icons={{
        error: <CircleAlert className="size-5" />,
        success: <CircleCheck className="size-5" />,
        info: <Info className="size-5" />,
        warning: <TriangleAlert className="size-5" />,
      }}
    />
  </>
)

// The Inertia page is the host page around every embedded driver, so a hook
// called outside any driver reads the host's transient bag from here.
const InertiaHostPage = ({ children }: { children: React.ReactNode }) => {
  const { transient } = usePage<{ transient: TransientData }>().props
  const hostPage = React.useMemo(() => ({ transient }), [transient])

  return <HostPageContext value={hostPage}>{children}</HostPageContext>
}

const compose =
  (...wrappers: React.ComponentType<{ children: React.ReactNode }>[]) =>
  ({ children }: { children: React.ReactNode }): React.ReactNode =>
    wrappers.reduceRight((node, Wrapper) => <Wrapper>{node}</Wrapper>, children)

const LayoutStack = compose(
  React.Suspense,
  InertiaHostPage,
  ZodConfig,
  ScrollerProvider,
  ToasterConfig,
  WorkspaceLayout,
  ShowHeader,
  AppContent,
  BodyLayout,
)

export function Layout({
  children,
  ...props
}: {
  children: React.ReactNode
} & LayoutContextValue) {
  return (
    <LayoutContext.Provider value={props}>
      <LayoutStack>{children}</LayoutStack>
    </LayoutContext.Provider>
  )
}
